import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DashboardService } from '../dashboard.service';
import { Router } from '@angular/router';
import { HeaderService } from 'src/app/header/header.service';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})

export class NotificationsComponent implements OnInit {
  notifications: any[] = [];
  filtered: any[] = [];
  public totalNotifications = 0;
  noNotifications = false;
  private page = 1;
  loading = false;
  filter: 'all' | 'unread' = 'all';

  constructor(
    private dashboardService: DashboardService,
    private toastr: ToastrService,
    private headerService: HeaderService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.getNotifications(1);
  }

  private getNotifications(pageNo) {
    if (this.loading) return; // Prevent multiple simultaneous requests

    this.loading = true;
    this.dashboardService.getNotifications(pageNo).subscribe({
      next: (res) => {
        if (res?.docs) {
          this.notifications = [...this.notifications, ...res.docs];
          this.applyFilter();
          this.noNotifications = this.filtered.length === 0;
        }
        // Support both mongoose-paginate-v2 (totalDocs) and legacy (total)
        this.totalNotifications = (res && (res.totalDocs ?? res.total)) || 0;
        this.notificationCount();
        this.loading = false;
      },
      error: (error) => {
        const errorMessage = error?.error?.errors?.[0]?.error || 'Failed to load notifications';
        this.toastr.error(errorMessage, 'Error');
        this.loading = false;
      }
    });
  }

  notificationRemove(notification): void {
    //TODO: remove notification.
  }

  viewNotification(notification) {
    // Optimistically mark as read on click
    const id = notification?._id || notification?.id;
    if (id && !notification.read) {
      notification.read = true;
      this.dashboardService.markNotifications([id], true).subscribe({
        next: () => {
          this.applyFilter();
          this.notificationCount();
        },
        error: () => {
          // Rollback local state if API fails
          notification.read = false;
        }
      });
    }
    this.router.navigate(['dashboard/assessments/questions-ans-preview/' + notification.assessmentId._id + '/' + notification.from]);
  }

  onScroll() {
    // Prevent scroll loading if already loading or no more notifications
    if (this.loading || this.notifications.length >= this.totalNotifications) {
      return;
    }

    this.page++;
    this.getNotifications(this.page);
  }

  notificationCount(): void {
    this.headerService.notificationCount().subscribe({
      next: (res) => {
        if (res?.count !== undefined) {
          this.headerService.notificationsCount$.next(res.count);
        }
      },
      error: (err) => {
        // Don't show error toast for notification count as it's not critical
        console.warn('Failed to update notification count:', err);
      }
    });
  }

  // New methods for the modern design
  markAllAsRead(): void {
    this.dashboardService.markAllNotificationsRead().subscribe({
      next: () => {
        // Update local state
        this.notifications = this.notifications.map(n => ({ ...n, read: true }));
        this.applyFilter();
        this.toastr.success('All notifications marked as read', 'Success');
        // Update bell count
        this.notificationCount();
      },
      error: () => this.toastr.error('Failed to mark all as read', 'Error'),
    });
  }

  refreshNotifications(): void {
    this.notifications = [];
    this.filtered = [];
    this.page = 1;
    this.getNotifications(1);
  }

  markAsRead(notification: any): void {
    if (notification.read) return;
    const id = notification._id || notification.id;
    if (!id) return;
    this.dashboardService.markNotifications([id], true).subscribe({
      next: () => {
        notification.read = true;
        this.applyFilter();
        this.toastr.success('Notification marked as read', 'Success');
        this.notificationCount();
      },
      error: () => this.toastr.error('Failed to mark as read', 'Error'),
    });
  }

  deleteNotification(notification: any): void {
    // TODO: Implement delete notification functionality
    const index = this.notifications.indexOf(notification);
    if (index > -1) {
      const id = notification._id || notification.id;
      if (!id) { return; }
      this.dashboardService.deleteNotifications([id]).subscribe({
        next: () => {
          this.notifications.splice(index, 1);
          this.applyFilter();
          // Adjust totals pessimistically
          this.totalNotifications = Math.max(0, this.totalNotifications - 1);
          this.toastr.success('Notification deleted', 'Success');
          this.notificationCount();
        },
        error: () => this.toastr.error('Failed to delete notification', 'Error')
      });
    }
  }

  trackByNotification(index: number, notification: any): any {
    return notification._id || index;
  }

  setFilter(value: 'all' | 'unread'): void {
    if (this.filter === value) return;
    this.filter = value;
    this.applyFilter();
  }

  private applyFilter(): void {
    this.filtered = this.filter === 'unread'
      ? this.notifications.filter(n => !n.read)
      : this.notifications.slice();
  }

  getNotificationType(notification: any): string {
    // Determine notification type based on content
    const text = notification.notification?.toLowerCase() || '';
    if (text.includes('submitted')) {
      return 'assessment-submitted';
    } else if (text.includes('completed')) {
      return 'assessment-completed';
    } else if (text.includes('reminder')) {
      return 'assessment-reminder';
    }
    return 'default';
  }

  getNotificationIcon(notification: any): string {
    // Return appropriate icon based on notification type
    const text = notification.notification?.toLowerCase() || '';
    if (text.includes('submitted')) {
      return 'assignment_turned_in';
    } else if (text.includes('completed')) {
      return 'task_alt';
    } else if (text.includes('reminder')) {
      return 'schedule';
    }
    return 'info';
  }
}
